import React, { useMemo } from "react";
import { useWebSocketContext } from "@/context/WebSocketContext";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "@heroui/react";

interface User {
  user_id: string;
  user_name: string;
  [key: string]: any;
}
interface OrderStatusProps {
  orderId: string;
  viewers: [];
}

function OrderStatusIndicator({ orderId }: Readonly<OrderStatusProps>) {
  const { ordersViewing } = useWebSocketContext();

  const isViewed = useMemo(
    () => ordersViewing.some((order) => order.order_id === orderId),
    [ordersViewing, orderId]
  );

  const { data: usersData, isLoading: isUsersLoading } = useQuery<{
    data: User[];
    pagination: any;
  }>({
    queryKey: ["AllUsers"],
    enabled: false,
  });

  const viewers = useMemo(() => {
    if (!usersData || !ordersViewing) return [];

    const orderViewers = ordersViewing.find((order) => order.order_id === orderId)?.viewers ?? [];

    return orderViewers
      ?.map((userId) => usersData.data.find((user: { user_id: string }) => user.user_id === userId))
      .filter((user): user is User => !!user);
  }, [usersData, ordersViewing, orderId]);

  const listViewers = useMemo(() => {
    if (isUsersLoading || viewers.length === 0) {
      return null;
    }
    return viewers.map((viewer) => (
      <span key={viewer.user_id}>
        {viewer.user_given_name} {viewer.user_name}
        <br />
      </span>
    ));
  }, [isUsersLoading, viewers]);

  return (
    <div className="flex items-center">
      {isViewed && (
        <div>
          <Tooltip content={listViewers}>
            <div className="w-3 h-3 rounded-full animate-colorChange" title="Viewed by users"></div>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default OrderStatusIndicator;
